import React, { useMemo } from "react";
import ReactSelect from "react-select";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@cmp-src/primitives/Select";
import { useIsMobile } from "@v2/hooks/use-mobile";
import { ArrowDownIcon } from "@v2/icons";

type Props = {
  floor: string | null;
  setFloor: React.Dispatch<React.SetStateAction<string | null>>;
  floors: { id: string | null; name: string }[];
  className?: string;
  classNamePrefix?: string;
  readonly?: boolean;
  customIcon?: React.ReactNode;
};

export default function FloorFilterDropdown({
  floors,
  setFloor,
  floor,
  className = "",
  classNamePrefix = "react-select",
  readonly = false,
  customIcon
}: Props) {
  const defaultFloor = {
    value: null,
    label: "-- All floors --"
  };
  const options = useMemo(() => {
    const floorValues = floors.map((x) => ({ value: x.id, label: x.name }));
    floorValues.unshift(defaultFloor);
    return floorValues;
  }, [floors]);
  const value = options.find((x) => x.value === floor);

  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Select
        value={value?.value || undefined}
        onValueChange={(id) => {
          if (readonly) {
            return;
          }
          setFloor((id === "all" ? "" : id) ?? "");
        }}
        disabled={readonly}
      >
        <SelectTrigger
          variant={"gray"}
          className={`mt-1 min-h-[42px] ${className}`}
        >
          <SelectValue className="truncate" placeholder="Select floor" />
          {customIcon || <ArrowDownIcon className="h-4 w-4 opacity-50" />}
        </SelectTrigger>
        <SelectContent>
          {options.map((floor) => (
            <SelectItem key={floor.value || "all"} value={floor.value || "all"}>
              {floor.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const DropdownIndicator = (props: any) => {
    return (
      <div
        {...props.innerProps}
        className="flex items-center justify-center p-2"
      >
        {customIcon || <ArrowDownIcon className="h-4 w-4 opacity-50" />}
      </div>
    );
  };

  return (
    <ReactSelect
      menuPortalTarget={document.body}
      menuPosition={"fixed"}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      isDisabled={readonly}
      className={className}
      onChange={(d, event) => {
        if (readonly) {
          return;
        }
        setFloor(d?.value ?? null);
      }}
      value={typeof value === "undefined" ? defaultFloor : value}
      options={options}
      classNamePrefix={classNamePrefix}
      components={{
        DropdownIndicator: customIcon ? DropdownIndicator : undefined
      }}
    />
  );
}
